import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import RadioGroup from 'components/ui/RadioGroup/RadioGroup';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import { sanitizeString, dateFormat } from 'utils/shared/utils';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoSupportUpdateModalProps {
  segment?: FullSegment;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (message: string, ticketId: number, commentPublic: boolean) => void;
  user?: any;
}

const MoSupportUpdateModal: FC<MoSupportUpdateModalProps> = ({
  segment,
  openModal,
  setOpenModal,
  user,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [message, setMessage] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [commentPublic, setCommentPublic] = useState(true);

  useEffect(() => {
    if (openModal) {
      setMessage('');
      setTicketId('');
      setCommentPublic(true);
    }
  }, [openModal]);

  return (
    <div style={{ direction: 'ltr' }}
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable"
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' mo-message-modal'} style={modalStyle}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            {t('mo.support_update')}{' '}
            {segment && (
              <Fragment>
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body text-xsm mo-message-modal-body">
          <div className="pb-10 mb-10 bb-1 text-sm flex-row" style={{ lineHeight: 2.7 }}>
            <span className="mr-10">
              <span className="mr-5">{t('mo.order_id')}:</span>
              <span className="mo-status mo-status-success">{segment?.orderId}</span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('mo.segment_id')}:</span>
              <span className="mo-status mo-status-success">{segment?.orderSegId}</span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('mo.check_in')}:</span>
              <span className="mo-status mo-status-success">
                {dateFormat(segment?.checkIn, 'dd-MMM-yy')}
              </span>
            </span>
            {!!segment?.suppPnr && (
              <span className="mr-10">
                <span className="mr-5">{t('mo.supplier_pnr')}:</span>
                <span className="mo-status mo-status-success">{segment?.suppPnr}</span>
              </span>
            )}
            <span className="mr-10">
              <span className="mr-5">{t('sort.name')}:</span>
              <span className="mo-status mo-status-success">
                {segment?.user_f_name} {segment?.user_l_name}
              </span>
            </span>
            <span className="mr-10">
              <span className="mr-5">{t('passenger.email')}:</span>
              <span className="mo-status mo-status-success">{user?.user_email}</span>
            </span>
          </div>
          <FormGroup
            label={t('mo.ticket_number')}
            value={ticketId}
            onChange={(val) => setTicketId(val)}
            allowOnlyNumbers
          />
          <div className="mb-10 mt-20">{t('mo.supplier_full')}</div>
          <RadioGroup
            options={[
              { label: 'mo.public', value: 'true' },
              { label: 'mo.private', value: 'false' },
            ]}
            value={commentPublic.toString()}
            onChange={(val) => setCommentPublic(val === 'true')}
            needToTranslate
          />

          <div className="mb-5 mt-10 text-xsm">{t('mo.body')}</div>
          <textarea
            className="form-control text-sm"
            onChange={(e) => setMessage(sanitizeString(e.target.value))}
            style={{ height: '150px', resize: 'none' }}
            value={message}
          />
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={() => props.onSubmit(message, +ticketId, commentPublic)}
          />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default MoSupportUpdateModal;
